import { palette } from '../design-tokens/palette';
import type { SemanticTokens } from '../design-tokens/semantic';

/**
 * Light Theme
 *
 * Maps semantic tokens to palette values for light mode.
 * This is the default theme optimized for business applications:
 * - High contrast for readability
 * - Calm neutrals to reduce eye fatigue
 * - Clear hierarchy
 */

export const lightTheme: SemanticTokens = {
  background: {
    app: '#fdf0e8', // Lightest gradient stop (also used as fallback)
    surface: palette.pure.white, // White for cards, modals
    surfaceSubtle: palette.neutral[100], // Table headers, subtle backgrounds
    subtle: palette.neutral[100], // Groups, pill toggles
    muted: palette.neutral[100], // Disabled/muted backgrounds
    input: palette.pure.white, // Input/select backgrounds
    hover: palette.neutral[100], // Row hover state
    selected: palette.warning[50], // Selected state with subtle warm tint
    overlay: 'rgba(0, 0, 0, 0.5)', // Modal backdrop
  },

  text: {
    primary: palette.neutral[900], // Almost black, high contrast
    secondary: palette.neutral[600], // Medium gray for supporting text
    muted: palette.neutral[400], // Light gray for placeholders
    tertiary: palette.neutral[400], // Very de-emphasized (locked items, disabled)
    inverse: palette.pure.white, // White text on dark backgrounds
    link: palette.warning[800], // Warm brown links
    linkHover: palette.warning[900], // Darker on hover
    brand: palette.warning[800], // Brand-colored text
    danger: palette.danger[600], // Danger/error text
    warning: palette.warning[600], // Warning text
  },

  border: {
    default: palette.neutral[200], // Standard border - subtle but visible
    subtle: palette.neutral[100], // Very subtle dividers
    strong: palette.neutral[300], // Emphasized borders
    focus: palette.warning[700], // Warm brown focus ring
    warning: palette.warning[400], // Warning state borders
  },

  accent: {
    // Primary (main CTAs, primary buttons) — warm brown
    primary: palette.warning[800],
    primaryHover: palette.warning[900],
    primaryActive: palette.warning[900],
    primarySubtle: palette.warning[100],

    // Secondary (secondary buttons, less emphasis)
    secondary: palette.neutral[700],
    secondaryHover: palette.neutral[800],
    secondaryActive: palette.neutral[900],

    // Success
    success: palette.success[600],
    successHover: palette.success[700],
    successSubtle: palette.success[100],

    // Warning
    warning: palette.warning[500],
    warningHover: palette.warning[600],
    warningSubtle: palette.warning[100],

    // Danger
    danger: palette.danger[600],
    dangerHover: palette.danger[700],
    dangerSubtle: palette.danger[100],
  },

  state: {
    disabled: palette.neutral[100],
    disabledText: palette.neutral[400],
    focus: palette.warning[700],
    selected: palette.warning[100],
    selectedText: palette.warning[900],
  },

  status: {
    // Success status (badges, indicators)
    successBg: palette.success[100],
    successText: palette.success[800],
    successBorder: palette.success[300],

    // Warning status
    warningBg: palette.warning[100],
    warningText: palette.warning[800],
    warningBorder: palette.warning[300],

    // Danger status
    dangerBg: palette.danger[100],
    dangerText: palette.danger[800],
    dangerBorder: palette.danger[300],

    // Info status
    infoBg: palette.primary[100],
    infoText: palette.primary[800],
  },

  badge: {
    blueBg: palette.primary[100],
    blueText: palette.primary[800],
    greenBg: palette.success[100],
    greenText: palette.success[800],
    redBg: palette.danger[100],
    redText: palette.danger[800],
    amberBg: palette.amber[100],
    amberText: palette.amber[800],
    purpleBg: palette.purple[100],
    purpleText: palette.purple[800],
    yellowBg: palette.warning[100],
    yellowText: palette.warning[800],
    skyBg: palette.sky[100],
    skyText: palette.sky[800],
    indigoBg: palette.indigo[100],
    indigoText: palette.indigo[800],
    emeraldBg: palette.emerald[100],
    emeraldText: palette.emerald[800],
    grayBg: palette.neutral[100],
    grayText: palette.neutral[700],
  },
};
